import React from "react";
import { formatMile } from "./formatters";

const ALERT_STYLES = {
  "Tornado Warning": {
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-400",
  },
  "Severe Thunderstorm Warning": {
    bg: "bg-orange-100 dark:bg-orange-900/30",
    text: "text-orange-700 dark:text-orange-400",
  },
  "Flash Flood Warning": {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-400",
  },
  "Winter Storm Warning": {
    bg: "bg-purple-100 dark:bg-purple-900/30",
    text: "text-purple-700 dark:text-purple-400",
  },
};

const DEFAULT_ALERT_STYLE = {
  bg: "bg-yellow-100 dark:bg-yellow-900/30",
  text: "text-yellow-700 dark:text-yellow-400",
};

/**
 * Map winter condition severity (0-12) to a color group.
 */
const getWinterSeverityStyle = (severity) => {
  if (severity == null)
    return { color: "text-gray-500", bg: "bg-gray-100 dark:bg-gray-700" };
  if (severity <= 1)
    return {
      color: "text-green-700 dark:text-green-400",
      bg: "bg-green-100 dark:bg-green-900/30",
    };
  if (severity <= 4)
    return {
      color: "text-yellow-700 dark:text-yellow-400",
      bg: "bg-yellow-100 dark:bg-yellow-900/30",
    };
  if (severity <= 8)
    return {
      color: "text-orange-700 dark:text-orange-400",
      bg: "bg-orange-100 dark:bg-orange-900/30",
    };
  return {
    color: "text-red-700 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-900/30",
  };
};

const WeatherSection = ({ weatherAlerts, winterConditions }) => {
  const hasAlerts = weatherAlerts && weatherAlerts.length > 0;
  const hasConditions = winterConditions && winterConditions.length > 0;

  if (!hasAlerts && !hasConditions) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        No weather alerts or winter conditions reported on this route.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {hasAlerts && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            Weather Alerts
          </p>
          <div className="space-y-2">
            {weatherAlerts.map((alert, i) => {
              const style = ALERT_STYLES[alert.type] || DEFAULT_ALERT_STYLE;
              const startMi = formatMile(alert.distanceAlongRouteMeters);
              const endMi = formatMile(alert.endDistanceAlongRouteMeters);
              const mileDisplay =
                startMi && endMi && startMi !== endMi
                  ? `mi ${startMi}–${endMi}`
                  : startMi
                    ? `mi ${startMi}`
                    : "";
              return (
                <div key={alert.id || i} className={`rounded p-2 ${style.bg}`}>
                  <div className="flex items-start gap-2">
                    <span
                      className={`text-xs font-semibold ${style.text} whitespace-nowrap`}
                    >
                      {alert.type || "Weather Alert"}
                    </span>
                    {mileDisplay && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto whitespace-nowrap">
                        {mileDisplay}
                      </span>
                    )}
                  </div>
                  {alert.headline && (
                    <p className="text-xs font-medium mt-1">{alert.headline}</p>
                  )}
                  {alert.summary && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                      {alert.summary}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasConditions && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            Winter Road Conditions
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-2 font-medium w-20">Miles</th>
                <th className="py-1 pr-2 font-medium">Condition</th>
                <th className="py-1 font-medium">Location</th>
              </tr>
            </thead>
            <tbody>
              {winterConditions.map((cond, i) => {
                const sevStyle = getWinterSeverityStyle(cond.severity);
                const startMi = formatMile(cond.distanceAlongRouteMeters);
                const endMi = formatMile(cond.endDistanceAlongRouteMeters);
                const mileDisplay =
                  startMi && endMi && startMi !== endMi
                    ? `${startMi}–${endMi}`
                    : startMi || "";
                return (
                  <tr key={cond.id || i} className="border-b last:border-0">
                    <td className="py-1 pr-2 text-gray-500 dark:text-gray-400 align-top whitespace-nowrap">
                      {mileDisplay}
                    </td>
                    <td className="py-1 pr-2 align-top">
                      <span
                        className={`px-1.5 py-0.5 rounded font-medium ${sevStyle.bg} ${sevStyle.color}`}
                      >
                        {cond.conditionType || "Unknown"}
                      </span>
                    </td>
                    <td className="py-1 text-gray-700 dark:text-gray-300">
                      {cond.locationDescription || ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WeatherSection;
